import React, { useEffect, useState } from "react";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { Col, Row, Alert } from "antd";
import {
	CalendarOutlined,
	UserOutlined,
	ExperimentOutlined,
	ApartmentOutlined,
	FormOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { User } from "../../../types/admin/UserTypes";
import {
	fetchPractitionerDashboard,
	PractitionerDashboardResponse,
	DashboardPendingForm,
} from "../../../api/healthcare/DashboardApi";
import { KPIItem } from "../components/DashboardKPIStrip";
import TodaySchedulePanel from "../components/TodaySchedulePanel";
import TreatmentPlansPanel from "../components/TreatmentPlansPanel";
import FormsInTrayPanel from "../components/FormsInTrayPanel";
import NewestPatientsPanel from "../components/NewestPatientsPanel";
import { hasFeature } from "../../../utils/subscriptions/subscriptionService";
import { useNotifications } from "../../../context/NotificationContext";

// Item modals opened directly from the dashboard (no navigation)
import PatientModal from "../../healthcare/patient/PatientModal";
import PlanDetailDrawer from "../../healthcare/treatmentPlans/planComponents/PlanDetailDrawer";
import ReviewFormModal from "../../healthcare/formsInTray/ReviewFormModal";
import AppointmentDrawer from "../../healthcare/appointments/AppointmentDrawer";

import { fetchPatient } from "../../../api/healthcare/PatientApi";
import { fetchPlan } from "../../../api/healthcare/TreatmentPlanApi";
import { getAssignedForms, AssignedForm } from "../../../api/formApi";
import { fetchSingleAppointmentById } from "../../../api/healthcare/Appointment/AppointmentApi";
import { fetchTeamMembers } from "../../../api/admin/teamApi";

import { Patient, defaultPatient } from "../../../types/healthcare/PatientTypes";
import { PatientTreatmentPlan } from "../../../types/healthcare/TreatmentPlanTypes";
import { Appointment } from "../../../types/appointments/AppointmentTypes";
import { TeamMember } from "../../../types/admin/TeamTypes";
import type { UserUuid } from "../../../types/identity";

interface PracDashboardProps {
	startDate: Date;
	endDate: Date;
	refreshToken?: number;
	onKPIChange?: (kpis: KPIItem[]) => void;
}

const PracDashboard: React.FC<PracDashboardProps> = ({ startDate, endDate, refreshToken = 0, onKPIChange }) => {
	const user: User = getSessionUser();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { showError } = useNotifications();

	const { data, isLoading, error } = useQuery<PractitionerDashboardResponse>({
		queryKey: ["practitioner-dashboard", startDate.toISOString(), endDate.toISOString(), refreshToken],
		queryFn: () =>
			fetchPractitionerDashboard({
				start_date: startDate.toISOString(),
				end_date: endDate.toISOString(),
			}),
		enabled: true,
	});

	const refreshDashboard = () => queryClient.invalidateQueries({ queryKey: ["practitioner-dashboard"] });

	// ── Item modals: fetch the full entity on click, then open in place ──────────
	const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
	const [patientOpen, setPatientOpen] = useState(false);
	const openPatient = async (id: UserUuid) => {
		try {
			const { data: patient } = await fetchPatient(id);
			setSelectedPatient(patient);
			setPatientOpen(true);
		} catch {
			showError("Failed to load patient details.");
		}
	};

	const [selectedPlan, setSelectedPlan] = useState<PatientTreatmentPlan | null>(null);
	const openPlan = async (id: number) => {
		try {
			const plan = await fetchPlan(id);
			// Attach the patient if the API response didn't embed it (mirrors the peptide dashboard)
			if (!plan.patient && plan.patient_id) {
				try {
					const res = await fetchPatient(String(plan.patient_id));
					(plan as any).patient = res.data;
				} catch {
					// non-critical — drawer degrades gracefully
				}
			}
			setSelectedPlan(plan);
		} catch {
			showError("Failed to load plan details.");
		}
	};

	const [currentForm, setCurrentForm] = useState<AssignedForm | null>(null);
	const openForm = async (form: DashboardPendingForm) => {
		try {
			const assigned = await getAssignedForms(form.patient_id);
			const match = assigned.find((f) => f.id === form.id) ?? null;
			if (!match) {
				showError("Form is no longer available.");
				return;
			}
			setCurrentForm(match);
		} catch {
			showError("Failed to load form.");
		}
	};

	// Appointment drawer — team members are only needed once an appointment is opened
	const [selectedAppointment, setSelectedAppointment] = useState<Appointment>();
	const [apptOpen, setApptOpen] = useState(false);
	const [apptPatient, setApptPatient] = useState<Patient>(defaultPatient);
	const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
	const [teamNeeded, setTeamNeeded] = useState(false);
	const { data: teamData } = useQuery({
		queryKey: ["dashboard-team-members"],
		queryFn: () => fetchTeamMembers(1, 30),
		enabled: teamNeeded,
	});
	const teamMembers = teamData?.data ?? [];
	const openAppointment = async (id: number) => {
		setTeamNeeded(true);
		try {
			const { data: result } = await fetchSingleAppointmentById(id);
			setSelectedAppointment(result.appointment);
			setApptOpen(true);
		} catch {
			showError("Failed to load appointment.");
		}
	};
	const handleStartConsultation = () => {
		// The full consultation flow lives on the appointments page
		setApptOpen(false);
		navigate("/healthcare/appointments");
	};

	// Peptide-only subscribers see a reduced dashboard (no results, no quick actions)
	const isPeptideOnly = hasFeature("healthcare.peptide") && !hasFeature("healthcare");

	useEffect(() => {
		if (isLoading || !data || !onKPIChange) return;
		const kpis: KPIItem[] = [
			...(!isPeptideOnly ? [{
				key: 'appointments',
				icon: <CalendarOutlined />,
				label: "Today's Appointments",
				value: data.appointments?.stats.today_count ?? 0,
				color: 'var(--mg-primary)',
			}] : []),
			{
				key: 'patients',
				icon: <UserOutlined />,
				label: 'Active Patients',
				value: data.patients?.active ?? 0,
				color: 'var(--mg-success)',
			},
			...(!isPeptideOnly ? [{
				key: 'results',
				icon: <ExperimentOutlined />,
				label: 'Pending Results',
				value: data.test_orders?.stats.results_available ?? 0,
				color: 'var(--mg-info)',
			}] : []),
			{
				key: 'plans',
				icon: <ApartmentOutlined />,
				label: 'Active Treatment Plans',
				value: data.treatment_plans?.stats.active ?? 0,
				color: 'var(--mg-warning)',
			},
			{
				key: 'forms',
				icon: <FormOutlined />,
				label: 'Forms Pending',
				value: data.forms?.stats.pending ?? 0,
				color: 'var(--mg-primary)',
			},
		];
		onKPIChange(kpis);
	}, [data, isLoading, isPeptideOnly, onKPIChange]);

	if (error) {
		return (
			<Alert
				type="error"
				message="Dashboard failed to load"
				description="Unable to fetch dashboard data. Please refresh the page or try again shortly."
				showIcon
			/>
		);
	}

	const appointments = data?.appointments;
	const testOrders = data?.test_orders;
	const treatmentPlans = data?.treatment_plans;
	const forms = data?.forms;
	const newestPatients = data?.newest_patients ?? [];
	const panelColumnProps = isPeptideOnly
		? { xs: 24 as const, sm: 12 as const, lg: 8 as const, xl: 8 as const }
		: { xs: 24 as const, sm: 12 as const, lg: 12 as const, xl: 6 as const };

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Row 1 — Critical results + treatment plans + newest patients + forms */}
			<Row gutter={[16, 16]}>
				{/* Will put the results in tray here eventually */}



				<Col {...panelColumnProps}>
					<TreatmentPlansPanel
						activePlans={treatmentPlans?.active_plans ?? []}
						stats={treatmentPlans?.stats ?? { total: 0, active: 0, paused: 0, completed: 0 }}
						isLoading={isLoading}
						onOpenPlan={openPlan}
					/>
				</Col>
				<Col {...panelColumnProps}>
					<NewestPatientsPanel
						newestPatients={newestPatients}
						isLoading={isLoading}
						onOpenPatient={openPatient}
					/>
				</Col>
				<Col {...panelColumnProps}>
					<FormsInTrayPanel
						pendingForms={forms?.pending_forms ?? []}
						stats={forms?.stats ?? { total: 0, pending: 0, in_progress: 0, completed: 0 }}
						isLoading={isLoading}
						onOpenForm={openForm}
					/>
				</Col>
			</Row>

			{/* Row 2 — Today's schedule */}
			{!isPeptideOnly && 
				<Row gutter={[16, 16]}>
					<Col xs={24} lg={16}>
						<TodaySchedulePanel
							appointments={appointments?.today_schedule ?? []}
							isLoading={isLoading}
							onOpenAppointment={openAppointment}
						/>
					</Col>
				</Row>
			}

			{/* Item modals */}
			{selectedPatient && (
				<PatientModal
					isOpen={patientOpen}
					onClose={() => {
						setPatientOpen(false);
						setSelectedPatient(null);
						refreshDashboard();
					}}
					patient={selectedPatient}
					onPatientUpdated={refreshDashboard}
				/>
			)}

			{selectedPlan && (
				<PlanDetailDrawer
					plan={selectedPlan}
					onClose={() => setSelectedPlan(null)}
					onPlanChange={refreshDashboard}
					showToolbar
				/>
			)}

			{currentForm && (
				<ReviewFormModal
					isOpen={!!currentForm}
					onClose={() => setCurrentForm(null)}
					onSave={() => {
						setCurrentForm(null);
						refreshDashboard();
					}}
					assignedForm={currentForm}
				/>
			)}

			{apptOpen && selectedAppointment && (
				<AppointmentDrawer
					user={user}
					isOpen={apptOpen}
					onClose={() => {
						setApptOpen(false);
						setSelectedTeamMember(null);
						refreshDashboard();
					}}
					onStartConsultation={handleStartConsultation}
					selectedAppointment={selectedAppointment}
					setSelectedAppointment={setSelectedAppointment}
					teamMembers={teamMembers}
					selectedTeamMember={selectedTeamMember}
					setSelectedTeamMember={setSelectedTeamMember}
					selectedPatient={apptPatient}
					setSelectedPatient={setApptPatient}
				/>
			)}
		</div>
	);
};

export default PracDashboard;
